import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  ArrowsAltOutlined,
} from '@ant-design/icons';
import { Button, Spin, Tooltip, Badge, Form, Modal } from 'antd';
import dayjs from 'dayjs';
import { Dialog, DialogTitle } from '@/components/dialog/Dialog';
import { API } from '@/services/api.service';

export default function UploadedAttachmentsList({
  attachments: initialAttachments,
  onMarkFileForDeletion,
  readOnly = false,
}) {
  const { t } = useTranslation(['messages', 'forms']);
  const [attachments, setAttachments] = useState(initialAttachments || []);
  const [deletingFile, setDeletingFile] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    setAttachments(initialAttachments || []);
  }, [initialAttachments]);

  const handleMarkForDelete = () => {
    onMarkFileForDeletion?.(deletingFile._id);
    setAttachments((prev) => prev.filter((f) => f._id !== deletingFile._id));
    setDeletingFile(null);
  };

  const handlePreview = async (file) => {
    try {
      setPreviewingId(file.key);

      const { url } = await API.files.downloadFile({ key: file.key });

      if (isImage(file.type)) {
        setModalContent({ type: 'image', url, filename: file.title });
        setModalOpen(true);
      } else if (isVideo(file.type)) {
        setModalContent({ type: 'video', url, filename: file.title });
        setModalOpen(true);
      } else if (isPDF(file.type)) {
        window.open(url, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = file.title || 'downloaded-file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to preview file:', error);
    } finally {
      setPreviewingId(null);
    }
  };

  const downloadCurrentModalFile = () => {
    if (!modalContent?.url) return;

    const link = document.createElement('a');
    link.href = modalContent.url;
    link.download = modalContent.filename || 'downloaded-file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImage = (mimeType) => {
    return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(
      mimeType
    );
  };

  const isVideo = (mimeType) => {
    return ['video/mp4', 'video/webm', 'video/ogg'].includes(mimeType);
  };

  const isPDF = (mimeType) => {
    return mimeType === 'application/pdf';
  };

  const getFriendlyFileTypeName = (mimeType) => {
    if (!mimeType) return '';
    if (mimeType.startsWith('image/')) return 'Image File';
    if (mimeType.startsWith('video/')) return 'Video File';
    if (mimeType.startsWith('application/pdf')) return 'PDF File';
    if (mimeType.includes('word')) return 'Word Document';
    if (mimeType.includes('excel')) return 'Excel Spreadsheet';
    if (mimeType.includes('zip')) return 'ZIP Archive';
    return 'File';
  };

  const getBadgeColor = (friendlyName) => {
    switch (friendlyName) {
      case 'PDF File':
        return '#f5222d';
      case 'Word Document':
        return '#1890ff';
      case 'Excel Spreadsheet':
        return '#52c41a';
      case 'ZIP Archive':
        return '#fa8c16';
      case 'Video File':
        return '#722ed1';
      default:
        return '#595959';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  if (!attachments?.length) {
    return (
      <div className="italic text-gray-500">
        {t('noUploadedFiles', { ns: 'messages' })}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-3">
        {attachments.map((file) => {
          const friendlyTypeName = getFriendlyFileTypeName(file.type);

          return (
            <div
              key={file._id}
              className="flex items-center justify-between rounded border bg-white p-2 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100">
                  {isImage(file.type) ? (
                    <EyeOutlined style={{ fontSize: '20px', color: '#555' }} />
                  ) : isVideo(file.type) ? (
                    <PlayCircleOutlined
                      style={{ fontSize: '24px', color: '#1890ff' }}
                    />
                  ) : (
                    <DownloadOutlined
                      style={{ fontSize: '20px', color: '#555' }}
                    />
                  )}
                </div>

                <div className="flex flex-col">
                  <Tooltip title={`${file.title} (${file.type})`}>
                    <span className="max-w-[400px] truncate font-medium">
                      {file.title}
                    </span>
                  </Tooltip>

                  <div className="mt-1 flex gap-2">
                    {friendlyTypeName !== 'Image File' && (
                      <Badge
                        count={friendlyTypeName}
                        style={{
                          backgroundColor: getBadgeColor(friendlyTypeName),
                          fontSize: '10px',
                        }}
                      />
                    )}

                    {file.updatedAt && (
                      <span className="text-xs text-gray-400">
                        {formatDate(file.updatedAt)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Tooltip title={t('previewDownload', { ns: 'messages' })}>
                  <Button
                    type="button"
                    className="btn-default-action !h-8 !w-8"
                    icon={
                      previewingId === file.key ? (
                        <Spin size="small" />
                      ) : (
                        <EyeOutlined />
                      )
                    }
                    onClick={() => handlePreview(file)}
                    disabled={
                      previewingId !== null && previewingId !== file.key
                    }
                  />
                </Tooltip>

                {!readOnly && (
                <Tooltip title={t('delete', { ns: 'messages' })}>
                  <Button
                    type="button"
                    className="btn-default-action !h-8 !w-8 !bg-red-500 text-white hover:!text-white"
                    icon={
                      deletingFile?._id === file._id ? (
                        <Spin
                          size="small"
                          className="[&_.ant-spin-dot-item]:bg-white"
                        />
                      ) : (
                        <DeleteOutlined />
                      )
                    }
                    onClick={() => setDeletingFile(file)}
                    disabled={
                      deletingFile !== null && deletingFile._id !== file._id
                    }
                  />
                </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        closable={false}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={modalContent?.type === 'video' ? 800 : 600}
        footer={
          <div className="flex justify-between">
            <Button
              type="default"
              className="btn-default-outlined"
              icon={<ArrowsAltOutlined />}
              onClick={() => window.open(modalContent.url, '_blank')}
            >
              {t('fullscreen', { ns: 'messages' })}
            </Button>

            <Button
              type="default"
              className="btn-primary"
              icon={<DownloadOutlined />}
              onClick={downloadCurrentModalFile}
            >
              {t('download', { ns: 'messages' })}
            </Button>
          </div>
        }
      >
        {modalContent?.type === 'image' && (
          <div className="flex items-center justify-center">
            <Spin spinning={!modalContent.url}>
              <img
                src={modalContent.url}
                alt="Preview"
                className="mx-auto max-h-[80vh] max-w-full rounded-md"
              />
            </Spin>
          </div>
        )}

        {modalContent?.type === 'video' && (
          <div className="flex items-center justify-center">
            <Spin spinning={!modalContent.url}>
              <video
                src={modalContent.url}
                controls
                className="mx-auto max-h-[80vh] w-full rounded-md"
              />
            </Spin>
          </div>
        )}
      </Modal>

      <Dialog
        title={t('deleteFile', { ns: 'messages' })}
        form={form}
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={!!deletingFile}
        okText={t('confirm', { ns: 'forms' })}
        cancelText={t('cancel', { ns: 'messages' })}
        onCancel={() => setDeletingFile(null)}
      >
        <Form form={form} onFinish={handleMarkForDelete}>
          <DialogTitle
            text={t('areYouSureDeleteFile', { ns: 'messages' })}
            dynamicText={deletingFile?.title || ''}
          />
        </Form>
      </Dialog>
    </>
  );
}
